import { CSV_CONTENT_TYPE, CSV_DOCUMENT_TYPE, DocumentType } from "./types";

/** smarta-documents rejects a decoded upload above 15 MiB. */
export const MAX_FILE_SIZE = 15 * 1024 * 1024;

const CONTENT_TYPE_BY_EXTENSION = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  csv: CSV_CONTENT_TYPE,
} as const;

export type UploadContentType =
  (typeof CONTENT_TYPE_BY_EXTENSION)[keyof typeof CONTENT_TYPE_BY_EXTENSION];

const SUPPORTED: ReadonlyArray<string> = Object.values(
  CONTENT_TYPE_BY_EXTENSION
);

/**
 * The content type sent to smarta-documents. Browsers report an empty or
 * odd type for some files (CSV on Windows is often `application/vnd.ms-excel`),
 * so the extension decides when the reported type is not one we accept.
 */
export const contentTypeOf = (file: File): UploadContentType | undefined => {
  const reported = file.type.toLowerCase();
  if (SUPPORTED.includes(reported)) return reported as UploadContentType;
  const extension = file.name.toLowerCase().split(".").pop() ?? "";
  return CONTENT_TYPE_BY_EXTENSION[
    extension as keyof typeof CONTENT_TYPE_BY_EXTENSION
  ];
};

/** The `filetype`s LocalAssetsViewer can show: a PDF embed or an image. */
export type PreviewType = "pdf" | "png" | "jpeg";

const PREVIEW_TYPE_BY_CONTENT_TYPE: Readonly<Record<string, PreviewType>> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpeg",
};

const PREVIEW_TYPE_BY_EXTENSION: Readonly<Record<string, PreviewType>> = {
  pdf: "pdf",
  png: "png",
  jpg: "jpeg",
  jpeg: "jpeg",
};

/**
 * How a stored file previews in the page, or `undefined` when it cannot and
 * must download instead (CSV, or anything unexpected). The content type
 * decides; a generic or odd one (parameters, `application/octet-stream`)
 * falls back to the file extension.
 */
export const previewTypeOf = ({
  contentType,
  fileName,
}: {
  contentType: string;
  fileName: string;
}): PreviewType | undefined => {
  const mediaType = contentType.split(";")[0].trim().toLowerCase();
  if (mediaType === CSV_CONTENT_TYPE) return undefined;
  const extension = fileName.toLowerCase().split(".").pop() ?? "";
  return (
    PREVIEW_TYPE_BY_CONTENT_TYPE[mediaType] ??
    PREVIEW_TYPE_BY_EXTENSION[extension]
  );
};

/** Extensions the file picker offers for a field. */
export const acceptFor = (documentType: DocumentType) =>
  documentType === CSV_DOCUMENT_TYPE
    ? ".pdf,.jpg,.jpeg,.png,.csv"
    : ".pdf,.jpg,.jpeg,.png";

/**
 * Whether a file may go into a field: a non-empty PDF, JPEG or PNG up to
 * 15 MiB anywhere, and CSV only in the bank statements field.
 */
export const isAcceptedFile = (documentType: DocumentType, file: File) => {
  const contentType = contentTypeOf(file);
  if (!contentType || file.size === 0 || file.size > MAX_FILE_SIZE) {
    return false;
  }
  return contentType !== CSV_CONTENT_TYPE || documentType === CSV_DOCUMENT_TYPE;
};
